import { useForm, Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { InertiaSharedProps } from "@/types/inertia";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";

type ForgotPasswordForm = {
  user: {
    email: string;
  };
};

interface ForgotPasswordProps {
  errors?: Record<string, string>;
}

export default function ForgotPassword({ errors = {} }: ForgotPasswordProps) {
  const {
    data,
    setData,
    post,
    processing,
    errors: formErrors,
  } = useForm<ForgotPasswordForm>({
    user: {
      email: "",
    },
  });
  const { flash } = usePage<InertiaSharedProps>().props;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    post("/forgot_password");
  };

  return (
    <AuthLayout
      title="Reset Password"
      description="Enter your email to reset your password"
    >
      <form onSubmit={submit}>
        <div className="flex flex-col gap-6">
          {/* Show flash messages */}
          {flash?.alert && (
            <Alert variant="destructive" className="bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{flash.alert}</AlertDescription>
            </Alert>
          )}
          {flash?.notice && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                {flash.notice}
              </AlertDescription>
            </Alert>
          )}

          {/* Show prop errors (for backwards compatibility) */}
          {errors && Object.keys(errors).length > 0 && (
            <Alert variant="destructive" className="bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errors.email || "Please check the information provided."}
              </AlertDescription>
            </Alert>
          )}

          <FormField
            id="email"
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={data.user.email}
            onChange={(value) => setData("user.email", value)}
            error={formErrors["user.email"]}
            required
            autoComplete="email"
          />

          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="submit"
              disabled={processing}
              variant="racing"
              size="lg"
              className="w-full shadow-xl"
            >
              {processing
                ? "Sending Instructions..."
                : "Send Reset Instructions"}
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">Remember your password?</span>{" "}
          <Link
            href="/login"
            className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
          >
            Sign In
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
